import { useTranslation } from "react-i18next";
import {
    ShieldCheckIcon,
    SignalIcon,
    EnvelopeIcon,
    LockClosedIcon,
    BugAntIcon,
    EyeIcon,
} from "@heroicons/react/20/solid";
import { Heading, Subheading } from "src/components/base/heading";
import { Text } from "src/components/base/text";
import { Badge } from "src/components/base/badge";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import type { View, ViewContext } from "../types";
import { VIEW_COLORS, VIEW_LABELS } from "../types";
import type { UnifiedEvent } from "../data/events";
import { UNIFIED_EVENTS } from "../data/events";
import {
    StatCard,
    Card,
    RingChart,
    Sparkline,
    FeedEntry,
    CorrelationBadges,
    IncidentChain,
    SectionTitle,
} from "../shared";

/* ── MTTR Gauge (semi-circle) ─────────────────────────────────────── */
function MttrGauge({ value, max = 15 }: { value: number; max?: number }) {
    const pct = Math.min(value / max, 1);
    const angle = -90 + pct * 180;
    const zoneArc = (start: number, end: number) => {
        const r = 70;
        const sx = 80 + r * Math.cos(((start - 90) * Math.PI) / 180);
        const sy = 80 + r * Math.sin(((start - 90) * Math.PI) / 180);
        const ex = 80 + r * Math.cos(((end - 90) * Math.PI) / 180);
        const ey = 80 + r * Math.sin(((end - 90) * Math.PI) / 180);
        const large = end - start > 180 ? 1 : 0;
        return `M ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`;
    };
    const needleX = 80 + 55 * Math.cos(((angle - 90) * Math.PI) / 180);
    const needleY = 80 + 55 * Math.sin(((angle - 90) * Math.PI) / 180);
    return (
        <div className="flex flex-col items-center">
            <svg viewBox="0 0 160 100" className="w-full max-w-[220px]">
                <defs>
                    <filter id="gaugeGlow">
                        <feGaussianBlur stdDeviation="2" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>
                {/* zone arcs */}
                <path
                    d={zoneArc(-90, -30)}
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth="10"
                    strokeLinecap="round"
                    opacity="0.35"
                />
                <path
                    d={zoneArc(-30, 30)}
                    fill="none"
                    stroke="#eab308"
                    strokeWidth="10"
                    strokeLinecap="round"
                    opacity="0.35"
                />
                <path
                    d={zoneArc(30, 90)}
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="10"
                    strokeLinecap="round"
                    opacity="0.35"
                />
                {/* needle */}
                <line
                    x1="80"
                    y1="80"
                    x2={needleX}
                    y2={needleY}
                    stroke="url(#needleGrad)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    filter="url(#gaugeGlow)"
                />
                <defs>
                    <linearGradient
                        id="needleGrad"
                        x1="80"
                        y1="80"
                        x2={String(needleX)}
                        y2={String(needleY)}
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop stopColor="#3b82f6" />
                        <stop offset="1" stopColor="#a855f7" />
                    </linearGradient>
                </defs>
                <circle cx="80" cy="80" r="4" fill="#3b82f6" />
                <text
                    x="80"
                    y="75"
                    textAnchor="middle"
                    className="fill-zinc-700 dark:fill-zinc-200"
                    fontSize="16"
                    fontWeight="800"
                >
                    {value}
                </text>
                <text x="80" y="92" textAnchor="middle" className="fill-zinc-400" fontSize="7">
                    min MTTR
                </text>
                {/* labels */}
                <text x="10" y="88" className="fill-zinc-400" fontSize="6">
                    &lt;5
                </text>
                <text x="75" y="14" className="fill-zinc-400" fontSize="6">
                    &lt;10
                </text>
                <text x="142" y="88" className="fill-zinc-400" fontSize="6">
                    &gt;10
                </text>
            </svg>
        </div>
    );
}

/* ── Alert Volume Heatmap 7x24 ────────────────────────────────────── */
function AlertHeatmap() {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    // Generate deterministic mock data
    const data: number[][] = [
        [2, 1, 0, 0, 0, 1, 3, 5, 12, 18, 22, 19, 15, 17, 20, 18, 14, 11, 8, 6, 5, 4, 3, 2],
        [1, 0, 1, 0, 0, 2, 4, 8, 15, 21, 24, 20, 16, 14, 19, 22, 17, 13, 9, 7, 4, 3, 2, 1],
        [3, 2, 1, 0, 0, 1, 3, 7, 14, 19, 21, 18, 14, 16, 18, 20, 15, 12, 8, 5, 4, 3, 2, 2],
        [1, 1, 0, 0, 0, 2, 5, 9, 16, 23, 28, 25, 18, 15, 21, 24, 19, 14, 10, 8, 5, 3, 2, 1],
        [2, 1, 1, 0, 0, 1, 4, 8, 13, 17, 20, 17, 13, 12, 16, 18, 14, 10, 7, 5, 3, 2, 1, 1],
        [0, 0, 0, 0, 0, 0, 1, 2, 4, 6, 8, 7, 5, 4, 5, 6, 4, 3, 2, 1, 0, 0, 0, 0],
        [0, 0, 0, 0, 0, 0, 1, 1, 3, 5, 6, 5, 4, 3, 4, 5, 3, 2, 1, 1, 0, 0, 0, 0],
    ];
    const maxVal = 28;
    return (
        <div className="overflow-x-auto">
            <div className="inline-block">
                <div className="mb-1 ml-8 flex">
                    {Array.from({ length: 24 }, (_, i) => (
                        <div key={i} className="w-4 text-center text-[7px] text-zinc-400">
                            {i}
                        </div>
                    ))}
                </div>
                {days.map((day, di) => (
                    <div key={day} className="flex items-center">
                        <span className="w-8 pr-1 text-right text-[9px] font-medium text-zinc-500">{day}</span>
                        {data[di].map((v, hi) => {
                            const intensity = v / maxVal;
                            return (
                                <div
                                    key={hi}
                                    className="h-3.5 w-4 rounded-[2px] border border-white/10 transition-all hover:z-10 hover:scale-150 dark:border-zinc-900/30"
                                    style={{
                                        backgroundColor:
                                            intensity === 0
                                                ? "rgba(59,130,246,0.04)"
                                                : `rgba(59,130,246,${0.15 + intensity * 0.85})`,
                                    }}
                                    title={`${day} ${hi}:00 — ${v} alerts`}
                                />
                            );
                        })}
                    </div>
                ))}
                <div className="ml-8 mt-2 flex items-center gap-1">
                    <span className="text-[8px] text-zinc-400">Less</span>
                    {[0.05, 0.2, 0.4, 0.65, 0.9].map((o, i) => (
                        <div
                            key={i}
                            className="h-2.5 w-4 rounded-[2px]"
                            style={{ backgroundColor: `rgba(59,130,246,${o})` }}
                        />
                    ))}
                    <span className="text-[8px] text-zinc-400">More</span>
                </div>
            </div>
        </div>
    );
}

/* ── Top Attackers Table ──────────────────────────────────────────── */
function TopAttackers() {
    const attackers = [
        {
            ip: "185.143.223.1",
            country: "RU",
            flag: "\u{1F1F7}\u{1F1FA}",
            events: 247,
            lastSeen: "14:23",
            blocked: true,
        },
        {
            ip: "91.215.85.12",
            country: "CN",
            flag: "\u{1F1E8}\u{1F1F3}",
            events: 189,
            lastSeen: "14:18",
            blocked: true,
        },
        {
            ip: "45.33.32.156",
            country: "US",
            flag: "\u{1F1FA}\u{1F1F8}",
            events: 134,
            lastSeen: "13:55",
            blocked: false,
        },
        {
            ip: "103.224.182.4",
            country: "KR",
            flag: "\u{1F1F0}\u{1F1F7}",
            events: 98,
            lastSeen: "13:41",
            blocked: true,
        },
        { ip: "77.88.55.80", country: "DE", flag: "\u{1F1E9}\u{1F1EA}", events: 67, lastSeen: "12:58", blocked: false },
    ];
    return (
        <div className="space-y-2.5">
            {attackers.map((a) => (
                <div
                    key={a.ip}
                    className="flex items-center gap-3 rounded-xl border border-zinc-200/30 bg-white/40 px-3 py-2 backdrop-blur-sm transition-all hover:bg-white/60 dark:border-zinc-700/30 dark:bg-zinc-800/40 dark:hover:bg-zinc-800/60"
                >
                    <span className="text-base">{a.flag}</span>
                    <div className="min-w-0 flex-1">
                        <span className="font-mono text-xs font-medium text-zinc-700 dark:text-zinc-300">{a.ip}</span>
                        <span className="ml-2 text-[10px] text-zinc-400">{a.country}</span>
                    </div>
                    <span className="font-mono text-xs font-bold text-zinc-600 dark:text-zinc-400">{a.events}</span>
                    <span className="text-[10px] text-zinc-400">{a.lastSeen}</span>
                    {a.blocked ? <Badge color="red">Blocked</Badge> : <Badge color="amber">Flagged</Badge>}
                </div>
            ))}
        </div>
    );
}

/* ── Detection Pipeline ───────────────────────────────────────────── */
function DetectionPipeline() {
    const steps = [
        { label: "Raw Events", value: 847, color: "from-blue-400/20 to-blue-500/20", border: "border-blue-400/30" },
        { label: "Filtered", value: 312, color: "from-blue-500/20 to-blue-600/20", border: "border-blue-500/30" },
        { label: "Correlated", value: 89, color: "from-blue-600/20 to-purple-500/20", border: "border-blue-600/30" },
        { label: "Alerts", value: 23, color: "from-purple-500/20 to-purple-600/20", border: "border-purple-500/30" },
        { label: "Incidents", value: 12, color: "from-purple-600/20 to-red-500/20", border: "border-purple-600/30" },
    ];
    return (
        <div className="flex flex-wrap items-center justify-center gap-2">
            {steps.map((step, i) => (
                <div key={step.label} className="flex items-center gap-2">
                    <div
                        className={`relative overflow-hidden rounded-xl border ${step.border} bg-gradient-to-br ${step.color} px-4 py-3 text-center backdrop-blur-sm transition-all hover:scale-105`}
                    >
                        <div className="absolute inset-0 bg-gradient-to-t from-white/5 to-white/20 dark:from-white/0 dark:to-white/5" />
                        <p className="relative bg-gradient-to-b from-zinc-800 to-zinc-600 bg-clip-text text-2xl font-extrabold text-transparent dark:from-white dark:to-zinc-400">
                            {step.value}
                        </p>
                        <p className="relative text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                            {step.label}
                        </p>
                    </div>
                    {i < steps.length - 1 && (
                        <svg className="h-4 w-6 shrink-0 text-blue-400/50" viewBox="0 0 24 16">
                            <path
                                d="M0 8h18m0 0l-5-5m5 5l-5 5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    )}
                </div>
            ))}
        </div>
    );
}

/* ── Response Team Status ─────────────────────────────────────────── */
function ResponseTeam() {
    const team = [
        {
            name: "M. Schmidt",
            role: "Analyst L3",
            status: "Online",
            statusColor: "bg-emerald-400",
            assignment: "INC-1247",
            initials: "MS",
            gradient: "from-blue-500 to-cyan-400",
        },
        {
            name: "L. Weber",
            role: "Analyst L2",
            status: "Busy",
            statusColor: "bg-red-400",
            assignment: "INC-1249",
            initials: "LW",
            gradient: "from-purple-500 to-pink-400",
        },
        {
            name: "T. Fischer",
            role: "Analyst L2",
            status: "Online",
            statusColor: "bg-emerald-400",
            assignment: "Triage Queue",
            initials: "TF",
            gradient: "from-blue-600 to-blue-400",
        },
        {
            name: "K. Braun",
            role: "Analyst L1",
            status: "Away",
            statusColor: "bg-amber-400",
            assignment: "Training",
            initials: "KB",
            gradient: "from-amber-500 to-orange-400",
        },
    ];
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {team.map((m) => (
                <div
                    key={m.name}
                    className="flex flex-col items-center rounded-xl border border-zinc-200/30 bg-white/40 p-3 backdrop-blur-sm transition-all hover:bg-white/60 dark:border-zinc-700/30 dark:bg-zinc-800/40 dark:hover:bg-zinc-800/60"
                >
                    <div className="relative">
                        <div
                            className={`flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br ${m.gradient} text-sm font-bold text-white shadow-lg`}
                        >
                            {m.initials}
                        </div>
                        <div
                            className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white dark:border-zinc-900 ${m.statusColor}`}
                        />
                    </div>
                    <p className="mt-2 text-xs font-semibold text-zinc-700 dark:text-zinc-300">{m.name}</p>
                    <p className="text-[10px] text-zinc-400">{m.role}</p>
                    <Badge
                        color={m.status === "Online" ? "green" : m.status === "Busy" ? "red" : "amber"}
                        className="mt-1.5"
                    >
                        {m.status}
                    </Badge>
                    <p className="mt-1 font-mono text-[10px] text-zinc-500">{m.assignment}</p>
                </div>
            ))}
        </div>
    );
}

/* ── Compliance Scorecard ─────────────────────────────────────────── */
function ComplianceScorecard() {
    const items = [
        { label: "SOC2", score: 94, color: "#3b82f6" },
        { label: "ISO27001", score: 91, color: "#6366f1" },
        { label: "PCI-DSS", score: 88, color: "#8b5cf6" },
        { label: "GDPR", score: 96, color: "#06b6d4" },
    ];
    return (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {items.map((item) => {
                const r = 32;
                const circ = 2 * Math.PI * r;
                const offset = circ * (1 - item.score / 100);
                return (
                    <div key={item.label} className="flex flex-col items-center">
                        <svg viewBox="0 0 80 80" className="h-20 w-20">
                            <circle
                                cx="40"
                                cy="40"
                                r={r}
                                fill="none"
                                stroke="currentColor"
                                className="text-zinc-200 dark:text-zinc-700"
                                strokeWidth="6"
                            />
                            <circle
                                cx="40"
                                cy="40"
                                r={r}
                                fill="none"
                                stroke={item.color}
                                strokeWidth="6"
                                strokeLinecap="round"
                                strokeDasharray={circ}
                                strokeDashoffset={offset}
                                transform="rotate(-90 40 40)"
                                style={{ filter: `drop-shadow(0 0 4px ${item.color}40)` }}
                            />
                            <text
                                x="40"
                                y="38"
                                textAnchor="middle"
                                className="fill-zinc-700 dark:fill-zinc-200"
                                fontSize="14"
                                fontWeight="800"
                            >
                                {item.score}%
                            </text>
                            <text
                                x="40"
                                y="52"
                                textAnchor="middle"
                                className="fill-zinc-400"
                                fontSize="7"
                                fontWeight="600"
                            >
                                {item.label}
                            </text>
                        </svg>
                    </div>
                );
            })}
        </div>
    );
}

/* ── Installation & License Coverage ────────────────────────────── */
function InstallationCoverage() {
    const booked = 350;
    const installed = 380;
    const isOverLimit = installed > booked;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-end justify-between">
                <div>
                    <Text className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                        License Utilization
                    </Text>
                    <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-zinc-900 dark:text-white">{installed}</span>
                        <span className="text-sm text-zinc-400">/ {booked} booked</span>
                    </div>
                </div>
                <Badge color={isOverLimit ? "red" : "green"}>
                    {isOverLimit ? `+${installed - booked} Overage` : "Compliant"}
                </Badge>
            </div>
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                <div
                    className={`h-full transition-all ${isOverLimit ? "bg-red-500" : "bg-blue-500"}`}
                    style={{ width: `${Math.min((installed / booked) * 100, 100)}%` }}
                />
            </div>
            <Text className="text-[11px] italic text-zinc-500">
                {isOverLimit
                    ? "Warning: Critical license overage detected across endpoint fleet."
                    : "Usage within allocated limits."}
            </Text>
        </div>
    );
}

/* ── Risky Users (Identity Risk) ────────────────────────────────── */
function RiskyUsers() {
    const users = [
        {
            name: "S. Miller",
            department: "Finance",
            risk: "Critical",
            reason: "14 failed O365 logins",
            time: "10m ago",
        },
        { name: "A. Novak", department: "DevOps", risk: "High", reason: "Impossible travel detected", time: "1h ago" },
        { name: "J. Doe", department: "Sales", risk: "Medium", reason: "New MFA device registered", time: "4h ago" },
    ];

    return (
        <div className="divide-y divide-zinc-950/5 dark:divide-white/5">
            {users.map((u) => (
                <div key={u.name} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <Text className="font-semibold text-zinc-900 dark:text-white">{u.name}</Text>
                            <Badge color={u.risk === "Critical" ? "red" : u.risk === "High" ? "orange" : "amber"}>
                                {u.risk}
                            </Badge>
                        </div>
                        <Text className="truncate text-xs text-zinc-500">{u.reason}</Text>
                    </div>
                    <div className="shrink-0 text-right">
                        <Text className="font-mono text-[10px] text-zinc-400">{u.time}</Text>
                        <Text className="text-[10px] text-zinc-500">{u.department}</Text>
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ── Software Vulnerabilities ─────────────────────────────────────── */
function VulnerabilitySummary() {
    const severities = [
        { label: "Critical", count: 12, color: "bg-red-500" },
        { label: "High", count: 48, color: "bg-orange-500" },
        { label: "Medium", count: 156, color: "bg-amber-500" },
        { label: "Low", count: 432, color: "bg-zinc-400" },
    ];

    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {severities.map((s) => (
                <div
                    key={s.label}
                    className="rounded-xl border border-zinc-200/50 bg-zinc-50/50 p-3 dark:border-zinc-700/50 dark:bg-zinc-900/50"
                >
                    <div className="flex items-center gap-1.5">
                        <div className={`h-2 w-2 rounded-full ${s.color}`} />
                        <Text className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{s.label}</Text>
                    </div>
                    <p className="mt-1 text-2xl font-bold text-zinc-900 dark:text-white">{s.count}</p>
                </div>
            ))}
        </div>
    );
}

/* ── Log Ingestion Health ─────────────────────────────────────────── */
function IngestionStatus() {
    const sources = [
        { name: "M365 Signals", status: "Healthy", color: "green", rate: "1.2 GB/s" },
        { name: "Edge Firewall", status: "Degraded", color: "orange", rate: "450 MB/s" },
        { name: "EDR Telemetry", status: "Healthy", color: "green", rate: "2.8 GB/s" },
        { name: "Internal DNS", status: "Offline", color: "red", rate: "0 B/s" },
    ];

    return (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {sources.map((s) => (
                <div
                    key={s.name}
                    className="flex items-center justify-between rounded-lg border border-zinc-200/30 bg-white/30 px-3 py-2 dark:border-zinc-700/30 dark:bg-zinc-800/30"
                >
                    <div className="flex flex-col">
                        <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">{s.name}</span>
                        <span className="font-mono text-[10px] text-zinc-400">{s.rate}</span>
                    </div>
                    <Badge color={s.color as any}>{s.status}</Badge>
                </div>
            ))}
        </div>
    );
}

/* ── Critical Vulnerabilities List (Filtered) ────────────────────── */
function CriticalVulnerabilityList() {
    const vulnerabilities = [
        {
            id: "CVE-2024-21413",
            name: "Microsoft Outlook RCE",
            severity: 9.8,
            system: "ws-102, ws-105",
            status: "Open",
        },
        {
            id: "CVE-2023-46604",
            name: "Apache ActiveMQ RCE",
            severity: 10.0,
            system: "srv-prod-04",
            status: "Patching",
        },
        { id: "CVE-2024-23113", name: "FortiOS Format String", severity: 9.8, system: "fw-edge-01", status: "Open" },
        {
            id: "CVE-2024-1597",
            name: "PostgreSQL Blind SQLi",
            severity: 9.3,
            system: "db-cluster-01",
            status: "Verified",
        },
    ];

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
                <Text className="text-xs font-medium text-zinc-500">
                    Showing <span className="font-bold text-red-600 dark:text-red-400">Critical</span> (CVSS &lt; 9.0)
                </Text>
                <Badge color="zinc" className="text-[10px]">
                    Filter: Software
                </Badge>
            </div>

            <Table className="[--gutter:theme(spacing.2)]" dense>
                <TableHead>
                    <TableRow>
                        <TableHeader>CVE / Name</TableHeader>
                        <TableHeader>Score</TableHeader>
                        <TableHeader>Affected</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {vulnerabilities.map((v) => (
                        <TableRow key={v.id} className="transition-colors hover:bg-zinc-50 dark:hover:bg-white/5">
                            <TableCell>
                                <div className="flex flex-col">
                                    <span className="font-mono text-[11px] font-bold text-zinc-900 dark:text-white">
                                        {v.id}
                                    </span>
                                    <span className="max-w-[140px] truncate text-[10px] text-zinc-500">{v.name}</span>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-1.5">
                                    <div className="h-1.5 w-1.5 rounded-full bg-red-500 shadow-[0_0_5px_rgba(239,68,68,0.5)]" />
                                    <span className="font-mono text-xs font-bold text-red-600 dark:text-red-400">
                                        {v.severity}
                                    </span>
                                </div>
                            </TableCell>
                            <TableCell className="font-mono text-[10px] text-zinc-600 dark:text-zinc-400">
                                {v.system}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

/* ── Main Dashboard ───────────────────────────────────────────────── */
export function SocDashboard({
    onNavigate,
    onInvestigate,
    onNavigateDetail,
}: {
    onNavigate: (v: View) => void;
    onInvestigate: (e: UnifiedEvent) => void;
    onNavigateDetail: (ctx: ViewContext) => void;
}) {
    const { t } = useTranslation();
    const socEvents = UNIFIED_EVENTS.filter((e) => e.system === "soc");
    const severityColor = { critical: "red", high: "orange", medium: "yellow", low: "zinc" } as const;
    const statusMap: Record<string, "open" | "investigating" | "resolved"> = {
        "SOC-1247": "open",
        "SOC-1248": "investigating",
        "SOC-1249": "investigating",
        "SOC-1250": "open",
        "SOC-1251": "resolved",
        "SOC-1252": "investigating",
    };
    const statusColor = { open: "red", investigating: "amber", resolved: "green" } as const;
    const typeMap: Record<string, string> = {
        "SOC-1247": t("soc.bruteForce"),
        "SOC-1248": t("soc.malware"),
        "SOC-1249": t("soc.dataExfil"),
        "SOC-1250": t("soc.suspiciousLogin"),
        "SOC-1251": t("soc.portScan"),
        "SOC-1252": t("soc.malware"),
    };
    const sourceMap: Record<string, string> = {
        "SOC-1247": "fw-prod-01",
        "SOC-1248": "edr-ws-142",
        "SOC-1249": "proxy-ext-02",
        "SOC-1250": "ad-dc-01",
        "SOC-1251": "ids-dmz-03",
        "SOC-1252": "edr-srv-08",
    };

    return (
        <>
            <Heading>{t("soc.title")}</Heading>

            {/* ── Stat Cards with Mini Sparklines ─────────────────────── */}
            <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard label={t("soc.openIncidents")} value="12" trend="+3 vs. yesterday" />
                <StatCard label={t("soc.threatsToday")} value="847" trend="+12%" />
                <StatCard label={t("soc.mttreact")} value="4.2 min" trend="-0.8 min" trendDown />
                <StatCard label={t("soc.mttresponse")} value="35 min" trend="-12 min" trendDown />
            </div>

            {/* ── Original 3-col grid ─────────────────────────────────── */}
            <div className="mt-8 grid gap-6 lg:grid-cols-2">
                <Card>
                    <Subheading>Cross-System Feed</Subheading>
                    <div className="mt-2 divide-y divide-zinc-950/5 dark:divide-white/5">
                        <FeedEntry
                            time="14:23"
                            icon={SignalIcon}
                            color="bg-blue-700 dark:bg-blue-400"
                            linkedSystem="ndr"
                            onNavigate={onNavigate}
                        >
                            Brute force from <span className="font-mono font-medium">185.143.223.1</span> — NDR blocked
                            lateral movement
                        </FeedEntry>
                        <FeedEntry
                            time="14:18"
                            icon={EnvelopeIcon}
                            color="bg-blue-400 dark:bg-blue-500"
                            linkedSystem="mail"
                            onNavigate={onNavigate}
                        >
                            Malware on ws-142 traced to Mail quarantine #Q-4821
                        </FeedEntry>
                        <FeedEntry
                            time="13:55"
                            icon={LockClosedIcon}
                            color="bg-blue-500"
                            linkedSystem="ztna"
                            onNavigate={onNavigate}
                        >
                            Data exfiltration — ZTNA session for s.braun terminated
                        </FeedEntry>
                        <FeedEntry
                            time="13:41"
                            icon={EnvelopeIcon}
                            color="bg-blue-400 dark:bg-blue-500"
                            linkedSystem="phishing"
                            onNavigate={onNavigate}
                        >
                            Suspicious login matches phishing campaign{" "}
                            <span className="font-medium">Q2 — IT Helpdesk</span>
                        </FeedEntry>
                        <FeedEntry
                            time="12:58"
                            icon={BugAntIcon}
                            color="bg-blue-700 dark:bg-blue-400"
                            linkedSystem="pentests"
                            onNavigate={onNavigate}
                        >
                            Malware on srv-08 exploits CVE from PT-004 — patch overdue
                        </FeedEntry>
                    </div>
                </Card>
                <Card>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            minHeight: "600px",
                        }}
                    >
                        <div>
                            <Subheading>Severity Distribution</Subheading>
                            <div className="mt-4 flex justify-center">
                                <RingChart
                                    label="847"
                                    segments={[
                                        { value: 23, color: "#1e3a5f", label: t("soc.critical") },
                                        { value: 89, color: "#2563eb", label: t("soc.high") },
                                        { value: 312, color: "#60a5fa", label: t("soc.medium") },
                                        { value: 423, color: "#bfdbfe", label: t("soc.low") },
                                    ]}
                                />
                            </div>
                        </div>
                        <div>
                            <Subheading>Threats (7 days)</Subheading>
                            <div className="mt-4">
                                <Sparkline data={[612, 748, 589, 834, 921, 756, 847]} />
                                <div className="mt-2 flex justify-between text-xs text-zinc-400">
                                    <span>Mon</span>
                                    <span>Tue</span>
                                    <span>Wed</span>
                                    <span>Thu</span>
                                    <span>Fri</span>
                                    <span>Sat</span>
                                    <span>Sun</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <Subheading>Alert Volume Heatmap</Subheading>
                            <div className="mt-4">
                                <AlertHeatmap />
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            {/* ── Recent Alerts Table (original) ──────────────────────── */}
            <Subheading className="mt-10">{t("soc.recentAlerts")}</Subheading>
            <Table className="mt-4" dense>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("soc.time")}</TableHeader>
                        <TableHeader>{t("soc.source")}</TableHeader>
                        <TableHeader>{t("soc.type")}</TableHeader>
                        <TableHeader>{t("soc.severity")}</TableHeader>
                        <TableHeader>{t("soc.status")}</TableHeader>
                        <TableHeader>Correlated</TableHeader>
                        <TableHeader />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {socEvents.map((e) => (
                        <TableRow
                            key={e.id}
                            className="cursor-pointer"
                            onClick={() => onNavigateDetail({ view: "soc-alert-detail", detailId: e.id })}
                        >
                            <TableCell className="font-mono text-zinc-500">{e.time}</TableCell>
                            <TableCell className="font-mono">{sourceMap[e.id] ?? "—"}</TableCell>
                            <TableCell>{typeMap[e.id] ?? e.title}</TableCell>
                            <TableCell>
                                <Badge color={severityColor[e.severity]}>{t(`soc.${e.severity}`)}</Badge>
                            </TableCell>
                            <TableCell>
                                <Badge color={statusColor[statusMap[e.id] ?? "open"]}>
                                    {t(`soc.${statusMap[e.id] ?? "open"}`)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <CorrelationBadges systems={e.correlated} onNavigate={onNavigate} />
                            </TableCell>
                            <TableCell>
                                <button
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        onInvestigate(e);
                                    }}
                                    className="text-zinc-400 hover:text-blue-600 dark:hover:text-blue-400"
                                >
                                    <EyeIcon className="h-4 w-4" />
                                </button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* ── New Stats Grid ────────────────────────────────────────────── */}
            <div className="mb mb-10 mt-10 grid gap-6 lg:grid-cols-2">
                <div className="space-y-6">
                    <SectionTitle title="Identity & Data Flow" description="Risky user behavior and ingestion health" />
                    <Card>
                        <Subheading className="mb-4">Risky Users (O365)</Subheading>
                        <RiskyUsers />
                    </Card>
                    <Card>
                        <Subheading className="mb-4">Log Ingestion Status</Subheading>
                        <IngestionStatus />
                    </Card>
                </div>
                <div className="space-y-6">
                    <SectionTitle
                        title="Inventory & Compliance"
                        description="Software licenses and vulnerability surface"
                    />
                    <Card>
                        <Subheading className="mb-4">Installation Coverage</Subheading>
                        <InstallationCoverage />
                    </Card>
                </div>
            </div>

            <Card>
                <div className="mb-4 flex items-center justify-between">
                    <Subheading>Detected Vulnerabilities</Subheading>
                    <button className="text-[10px] font-semibold text-blue-600 hover:text-blue-500 dark:text-blue-400">
                        View All Scan Reports →
                    </button>
                </div>

                {/* The numeric summary boxes first */}
                <VulnerabilitySummary />

                <div className="mt-6 border-t border-zinc-950/5 pt-6 dark:border-white/5">
                    {/* The filtered list of critical CVEs */}
                    <CriticalVulnerabilityList />
                </div>
            </Card>

            {/* ── Detection Pipeline ──────────────────────────────────── */}
            <SectionTitle title="Detection Pipeline" description="Event flow from ingestion to incident" />
            <Card>
                <DetectionPipeline />
            </Card>

            {/* ── Top Attackers ────────────────────────────────────────── */}
            <SectionTitle title="Top Attackers" description="Most active source IPs in the last 24 hours" />
            <Card>
                <TopAttackers />
            </Card>

            {/* ── Compliance Scorecard ─────────────────────────────────── */}
            <SectionTitle title="Compliance Scorecard" description="Framework compliance status" />
            <Card>
                <ComplianceScorecard />
            </Card>
        </>
    );
}
